<script>
/**
 * @typedef {Object} Props
 * @property {boolean} [article]
 * @property {any} author
 * @property {{ name: string; slug: string }[]} breadcrumbs
 * @property {any} datePublished
 * @property {any} lastUpdated
 * @property {any} featuredImage
 * @property {any} metaDescription
 * @property {any} contactEmail
 * @property {any} siteLanguage
 * @property {any} siteDescription
 * @property {any} siteTitle
 * @property {any} siteUrl
 * @property {any} title
 * @property {any} url
 * @property {any} facebookPage
 * @property {any} linkedinProfile
 * @property {{ url: string; faviconWidth: number; faviconHeight: number } | null} [entityMeta]
 */

/** @type {Props} */
let {
    article = false,
    author,
    breadcrumbs,
    datePublished,
    lastUpdated,
    featuredImage,
    metaDescription,
    contactEmail,
    siteLanguage,
    siteDescription,
    siteTitle,
    siteUrl,
    title,
    url,
    facebookPage,
    linkedinProfile,
    entityMeta = null,
} = $props();

const schemaOrgEntity = $derived(
    entityMeta !== null
        ? {
              '@type': 'Organization',
              '@id': `${siteUrl}/#organization`,
              url: siteUrl,
              name: author,
              logo: entityMeta.url,
              email: contactEmail,
              description: siteDescription,
              image: {
                  '@type': 'ImageObject',
                  '@id': `${siteUrl}/#personlogo`,
                  inLanguage: siteLanguage,
                  url: entityMeta.url,
                  width: entityMeta.faviconWidth,
                  height: entityMeta.faviconHeight,
                  caption: author,
              },
              sameAs: [linkedinProfile, facebookPage],
          }
        : null,
);

let schemaOrgWebsite = $derived({
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    url: siteUrl,
    name: siteTitle,
    description: siteDescription,
    publisher: {
        '@id': `${siteUrl}/#organization`,
    },
    potentialAction: [],
    inLanguage: siteLanguage,
});

const schemaOrgImageObject = $derived({
    '@type': 'ImageObject',
    '@id': `${url}#primaryimage`,
    inLanguage: siteLanguage,
    url: featuredImage.url,
    contentUrl: featuredImage.url,
    width: featuredImage.width,
    height: featuredImage.height,
    caption: featuredImage.caption,
});

let schemaOrgBreadcrumbList = $derived({
    '@type': 'BreadcrumbList',
    '@id': `${url}#breadcrumb`,
    itemListElement: breadcrumbs.map((element, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
            '@type': 'WebPage',
            '@id': `${siteUrl}${element.slug}`,
            url: `${siteUrl}${element.slug}`,
            name: element.name,
        },
    })),
});

const schemaOrgWebPage = $derived({
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: title,
    isPartOf: {
        '@id': `${siteUrl}/#website`,
    },
    primaryImageOfPage: {
        '@type': 'ImageObject',
        url: featuredImage.url,
        width: featuredImage.width,
        height: featuredImage.height,
    },
    datePublished,
    dateModified: lastUpdated,
    author: {
        '@id': `${siteUrl}/#organization`,
    },
    description: metaDescription,
    breadcrumb: {
        '@id': `${url}#breadcrumb`,
    },
    inLanguage: siteLanguage,
    potentialAction: [
        {
            '@type': 'ReadAction',
            target: [url],
        },
    ],
});

/** @type {Record<string, unknown> | null} */
const schemaOrgArticle = $derived(
    article
        ? {
              '@type': 'Article',
              '@id': `${url}#article`,
              isPartOf: {
                  '@id': `${url}#webpage`,
              },
              author: {
                  '@id': `${siteUrl}/#organization`,
              },
              headline: title,
              datePublished,
              dateModified: lastUpdated,
              mainEntityOfPage: {
                  '@id': `${url}#webpage`,
              },
              publisher: {
                  '@id': `${siteUrl}/#organization`,
              },
              image: {
                  '@id': `${url}#primaryimage`,
              },
              articleSection: ['blog'],
              inLanguage: siteLanguage,
          }
        : null,
);

let schemaOrgArray = $derived([
    schemaOrgEntity,
    schemaOrgWebsite,
    ...(article ? [schemaOrgImageObject] : []),
    schemaOrgWebPage,
    schemaOrgBreadcrumbList,
    ...(article ? [schemaOrgArticle] : []),
]);
let schemaOrgObject = $derived({
    '@context': 'https://schema.org',
    '@graph': schemaOrgArray,
});
let jsonLdString = $derived(JSON.stringify(schemaOrgObject));
let jsonLdScript = $derived(`
		<script type="application/ld+json">
			${jsonLdString}
		${'<'}/script>
	`);
</script>

<svelte:head>
    {@html jsonLdScript}
</svelte:head>
